import React, { useEffect, useRef, useState } from 'react';
import { Modal } from '../../components/Modal';
import { ModalFooter } from '../../components/ModalFooter';
import { FormGroup } from '../../components/FormGroup';
import { MsComboBox } from '../../components/MsComboBox';
import { useProjectUsers, type ProjectAccount } from '../../hooks/useProjectUsers';
import { useProjectRoles, type Role } from '../../hooks/useProjectRoles';
import { useAssignBatch, type Batch } from '../../hooks/useProjectBatches';

interface Props {
  projectId: string;
  batch: Batch;
  onClose: () => void;
  onAssigned: () => void;
}

export function AssignBatchModal({ projectId, batch, onClose, onAssigned }: Props) {
  const { data: users = [], isSuccess: usersReady } = useProjectUsers(projectId);
  const { data: roles = [], isSuccess: rolesReady } = useProjectRoles(projectId);
  const assignBatch = useAssignBatch(projectId);

  const [selectedUsers, setSelectedUsers] = useState<ProjectAccount[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<Role[]>([]);
  const hydrated = useRef(false);

  useEffect(() => {
    if (hydrated.current || !usersReady || !rolesReady) return;
    const batchUserIds = new Set(batch.users.map(u => u.id));
    const batchRoleNames = new Set(batch.roles.map(r => r.name));
    setSelectedUsers(users.filter(u => batchUserIds.has(u.id)));
    setSelectedRoles(roles.filter(r => batchRoleNames.has(r.name)));
    hydrated.current = true;
  }, [usersReady, rolesReady]);

  const handleSubmit = () => {
    assignBatch.mutate(
      {
        id: batch.id,
        keepFamiliesTogether: true,
        delimiter: '_',
        startValue: 1,
        zeroPaddingLength: 3,
        filesPerBatch: 200,
        users: selectedUsers,
        roles: selectedRoles,
      },
      { onSuccess: onAssigned },
    );
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSubmit();
  };

  return (
    <Modal
      title={`Assign Batch: ${batch.name}`}
      icon="fas fa-tags"
      onClose={onClose}
      onSubmit={handleFormSubmit}
      formClassName="form-horizontal"
      footer={
        <ModalFooter
          onClose={onClose}
          nextLabel="Save"
          saving={assignBatch.isPending}
          savingLabel="Saving…"
        />
      }
    >
      <FormGroup label="Users" htmlFor="assign-users" labelCols="col-sm-2" inputCols="col-sm-10">
        <MsComboBox<ProjectAccount>
          id="assign-users"
          items={users}
          selected={selectedUsers}
          onChange={setSelectedUsers}
          getKey={u => u.id}
          getLabel={u => u.fullName}
          icon="fa-user"
        />
      </FormGroup>
      <FormGroup label="Roles" htmlFor="assign-roles" labelCols="col-sm-2" inputCols="col-sm-10">
        <MsComboBox<Role>
          id="assign-roles"
          items={roles}
          selected={selectedRoles}
          onChange={setSelectedRoles}
          getKey={r => r.id}
          getLabel={r => r.name}
          icon="fa-users"
        />
      </FormGroup>
      {assignBatch.isError && (
        <p className="text-danger">Failed to assign batch. Please try again.</p>
      )}
    </Modal>
  );
}
